// frontend/src/utils/statusLabels.ts
// Единая локализация статусов для проекта SSTO
// Версия: 1.2.3 (2026-02-21)
// Изменения:
// - Добавлен статус заявки "completed" (ВЫПОЛНЕНА)
// - Добавлен экспорт getAlertStatusLabel (alias для getSignalTypeLabel)
// - Добавлен статус сигнала "confirmed" (ПОДТВЕРЖДЁН) — регрессия после Bug3 fix

type MuiColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

// === СТАТУСЫ СИГНАЛОВ ===
export const SIGNAL_STATUS = {
  MATCHED: 'matched',
  UNMATCHED: 'unmatched',
  ERROR: 'error',
  NO_MATCH: 'no_match',
  DUPLICATE: 'duplicate',
  ARCHIVED: 'archived',
  PENDING_MANUAL: 'pending_manual',
  CONFIRMED: 'confirmed',
} as const;

export type SignalStatusType = (typeof SIGNAL_STATUS)[keyof typeof SIGNAL_STATUS];

export const SIGNAL_STATUS_LABELS: Record<SignalStatusType, string> = {
  matched: 'СОПОСТАВЛЕН',
  unmatched: 'НЕ СОПОСТАВЛЕН',
  error: 'ОШИБКА',
  no_match: 'НЕТ СОВПАДЕНИЯ',
  duplicate: 'ДУБЛИКАТ',
  archived: 'АРХИВ',
  pending_manual: 'ОЖИДАЕТ ОПЕРАТОРА',
  confirmed: 'ПОДТВЕРЖДЁН',
};

export const SIGNAL_STATUS_LABELS_SHORT: Record<SignalStatusType, string> = {
  matched: 'СОП.',
  unmatched: 'НЕСОП.',
  error: 'ОШ.',
  no_match: 'НЕТ',
  duplicate: 'ДУБЛ.',
  archived: 'АРХ.',
  pending_manual: 'ОЖ.ОП.',
  confirmed: 'ПОДТВ.',
};

export const SIGNAL_STATUS_COLORS: Record<SignalStatusType, MuiColor> = {
  matched: 'success',
  unmatched: 'warning',
  error: 'error',
  no_match: 'default',
  duplicate: 'info',
  archived: 'default',
  pending_manual: 'warning',
  confirmed: 'success',
};

export const SIGNAL_STATUS_HEX_COLORS: Record<SignalStatusType, string> = {
  matched: '#4caf50',
  unmatched: '#ff9800',
  error: '#f44336',
  no_match: '#757575',
  duplicate: '#2196f3',
  archived: '#9e9e9e',
  pending_manual: '#ff9800',
  confirmed: '#4caf50',
};

export function getSignalStatusLabel(status: string, short = false): string {
  const key = String(status || '').toLowerCase() as SignalStatusType;
  if (short) return SIGNAL_STATUS_LABELS_SHORT[key] || 'НЕИЗВ.';
  return SIGNAL_STATUS_LABELS[key] || 'НЕИЗВЕСТНО';
}

export function getSignalStatusColor(status: string): MuiColor {
  const key = String(status || '').toLowerCase() as SignalStatusType;
  return SIGNAL_STATUS_COLORS[key] || 'default';
}

export function getSignalStatusHexColor(status: string): string {
  const key = String(status || '').toLowerCase() as SignalStatusType;
  return SIGNAL_STATUS_HEX_COLORS[key] || '#757575';
}

// === СТАТУСЫ ЗАЯВОК ===
export const REQUEST_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  PROCESSING: 'processing',
  IN_TESTING: 'in_testing',
  APPROVED: 'approved',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  SUCCESSFUL: 'successful',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  CANCELED: 'canceled',
  REJECTED: 'rejected',
  UNSUCCESSFUL: 'unsuccessful',
  EXPIRED: 'expired',
} as const;

export type RequestStatusType = (typeof REQUEST_STATUS)[keyof typeof REQUEST_STATUS];

export const REQUEST_STATUS_LABELS: Record<RequestStatusType, string> = {
  pending: 'ОЖИДАЕТ',
  active: 'АКТИВНА',
  processing: 'В ОБРАБОТКЕ',
  in_testing: 'В ТЕСТЕ',
  approved: 'ОДОБРЕНА',
  confirmed: 'ПОДТВЕРЖДЕНА',
  completed: 'ВЫПОЛНЕНА',
  successful: 'УСПЕШНО',
  failed: 'ОШИБКА',
  cancelled: 'ОТМЕНЕНА',
  canceled: 'ОТМЕНЕНА',
  rejected: 'ОТКЛОНЕНА',
  unsuccessful: 'НЕУСПЕШНЫЙ',
  expired: 'ПРОСРОЧЕНА',
};

export const REQUEST_STATUS_LABELS_SHORT: Record<RequestStatusType, string> = {
  pending: 'ОЖ.',
  active: 'АКТ.',
  processing: 'ОБР.',
  in_testing: 'ТЕСТ',
  approved: 'ОДОБ.',
  confirmed: 'ПОДТВ.',
  completed: 'ВЫП.',
  successful: 'УСП.',
  failed: 'ОШ.',
  cancelled: 'ОТМ.',
  canceled: 'ОТМ.',
  rejected: 'ОТКЛ.',
  unsuccessful: 'НЕУСП.',
  expired: 'ПРОСР.',
};

export const REQUEST_STATUS_COLORS: Record<RequestStatusType, MuiColor> = {
  pending: 'warning',
  active: 'info',
  processing: 'info',
  in_testing: 'secondary',
  approved: 'info',
  confirmed: 'success',
  completed: 'success',
  successful: 'success',
  failed: 'error',
  cancelled: 'default',
  canceled: 'default',
  rejected: 'error',
  unsuccessful: 'error',
  expired: 'default',
};

export const REQUEST_STATUS_HEX_COLORS: Record<RequestStatusType, string> = {
  pending: '#ff9800',
  active: '#2196f3',
  processing: '#2196f3',
  in_testing: '#9c27b0',
  approved: '#2196f3',
  confirmed: '#4caf50',
  completed: '#4caf50',
  successful: '#4caf50',
  failed: '#f44336',
  cancelled: '#757575',
  canceled: '#757575',
  rejected: '#f44336',
  unsuccessful: '#f44336',
  expired: '#9e9e9e',
};

// === МАППИНГ СТАТУСОВ (13+ → 6) ===
const STATUS_MAPPING: Record<string, RequestStatusType> = {
  // → pending
  draft: 'pending',
  submitted: 'pending',
  in_testing: 'pending',
  in_review: 'pending',
  awaiting_signal: 'pending',
  waiting_signal: 'pending',
  active: 'pending',
  processing: 'pending',
  pending: 'pending',

  // → approved
  approved: 'approved',
  matched: 'approved',
  signal_received: 'approved',

  // → confirmed
  confirmed: 'confirmed',
  completed: 'confirmed',
  successful: 'confirmed',

  // → failed
  failed: 'failed',

  // → cancelled
  cancelled: 'cancelled',
  canceled: 'cancelled',

  // → rejected
  rejected: 'rejected',

  // → unsuccessful
  unsuccessful: 'failed',

  // → expired
  expired: 'expired',
};

export function normalizeRequestStatus(status: string): RequestStatusType {
  const key = String(status || '')
    .toLowerCase()
    .trim();
  return STATUS_MAPPING[key] || 'pending';
}

export function getRequestStatusLabel(status: string, short = false): string {
  const normalized = normalizeRequestStatus(status);
  if (short) return REQUEST_STATUS_LABELS_SHORT[normalized] || 'НЕИЗВ.';
  return REQUEST_STATUS_LABELS[normalized] || 'НЕИЗВЕСТНО';
}

export function getRequestStatusColor(status: string): MuiColor {
  const normalized = normalizeRequestStatus(status);
  return REQUEST_STATUS_COLORS[normalized] || 'default';
}

export function getRequestStatusHexColor(status: string): string {
  const normalized = normalizeRequestStatus(status);
  return REQUEST_STATUS_HEX_COLORS[normalized] || '#757575';
}

// === ТИПЫ СИГНАЛОВ ===
export const SIGNAL_TYPE = {
  SSAS_TEST: 'SSAS_TEST',
  SSAS_ALERT: 'SSAS_ALERT',
  REAL_ALERT: 'REAL_ALERT',
  SECURITY: 'SECURITY',
  DISTRESS: 'DISTRESS',
  EPIRB: 'EPIRB',
  VHF_DSC: 'VHF_DSC',
  EMAIL: 'EMAIL',
  UNKNOWN: 'UNKNOWN',
} as const;

export type SignalTypeType = (typeof SIGNAL_TYPE)[keyof typeof SIGNAL_TYPE];

export const SIGNAL_TYPE_LABELS: Record<SignalTypeType, string> = {
  SSAS_TEST: '🧪 ТЕСТ ССТО',
  SSAS_ALERT: '🚨 ТРЕВОГА ССТО',
  REAL_ALERT: '🚨 РЕАЛЬНАЯ ТРЕВОГА',
  SECURITY: '🚨 SECURITY',
  DISTRESS: '🚨 DISTRESS',
  EPIRB: '🛰️ EPIRB',
  VHF_DSC: '📻 VHF DSC',
  EMAIL: '✉️ EMAIL',
  UNKNOWN: '❓ НЕИЗВЕСТНО',
};

export const SIGNAL_TYPE_COLORS: Record<SignalTypeType, MuiColor> = {
  SSAS_TEST: 'warning',
  SSAS_ALERT: 'error',
  REAL_ALERT: 'error',
  SECURITY: 'error',
  DISTRESS: 'error',
  EPIRB: 'error',
  VHF_DSC: 'error',
  EMAIL: 'info',
  UNKNOWN: 'default',
};

export const SIGNAL_TYPE_HEX_COLORS: Record<SignalTypeType, string> = {
  SSAS_TEST: '#ff9800',
  SSAS_ALERT: '#f44336',
  REAL_ALERT: '#f44336',
  SECURITY: '#f44336',
  DISTRESS: '#f44336',
  EPIRB: '#f44336',
  VHF_DSC: '#f44336',
  EMAIL: '#2196f3',
  UNKNOWN: '#757575',
};

export function getSignalTypeLabel(type: string): string {
  const key = String(type || '').toUpperCase() as SignalTypeType;
  return SIGNAL_TYPE_LABELS[key] || '❓ НЕИЗВЕСТНО';
}

export function getSignalTypeColor(type: string): MuiColor {
  const key = String(type || '').toUpperCase() as SignalTypeType;
  return SIGNAL_TYPE_COLORS[key] || 'default';
}

export function getSignalTypeHexColor(type: string): string {
  const key = String(type || '').toUpperCase() as SignalTypeType;
  return SIGNAL_TYPE_HEX_COLORS[key] || '#757575';
}

export function isRealAlert(type: string): boolean {
  const t = String(type || '').toUpperCase();
  return t === 'SSAS_ALERT' || t === 'REAL_ALERT' || t === 'SECURITY' || t === 'DISTRESS';
}

// Backward compatibility: некоторые компоненты ожидают getAlertStatusLabel()
export function getAlertStatusLabel(type: string): string {
  return getSignalTypeLabel(type);
}

export default {
  SIGNAL_STATUS,
  SIGNAL_STATUS_LABELS,
  SIGNAL_STATUS_LABELS_SHORT,
  SIGNAL_STATUS_COLORS,
  SIGNAL_STATUS_HEX_COLORS,
  getSignalStatusLabel,
  getSignalStatusColor,
  getSignalStatusHexColor,
  REQUEST_STATUS,
  REQUEST_STATUS_LABELS,
  REQUEST_STATUS_LABELS_SHORT,
  REQUEST_STATUS_COLORS,
  REQUEST_STATUS_HEX_COLORS,
  getRequestStatusLabel,
  getRequestStatusColor,
  getRequestStatusHexColor,
  SIGNAL_TYPE,
  SIGNAL_TYPE_LABELS,
  SIGNAL_TYPE_COLORS,
  SIGNAL_TYPE_HEX_COLORS,
  getSignalTypeLabel,
  getSignalTypeColor,
  getSignalTypeHexColor,
  isRealAlert,
  getAlertStatusLabel,
};
